<script setup>
import SurveyBox from "./SurveyBox.vue";
import HomeSkillBuilding from "./HomeSkillBuilding.vue";
import HomeCohortReports from "./HomeCohortReports.vue";
import HomeFacultyReport from "./HomeFacultyReport.vue";
import HomeAdminTasks from "./HomeAdminTasks.vue";
import HomeReports from "./HomeReports.vue";
import HomePDF from "./HomePDF.vue";
import ReportFilterPanel from "../components/ReusableComponents/ReportFilterComponent.vue";
import ExecutiveSidebar from "./ExecutiveSidebar.vue";
</script>

<template>
  <div class="body-form-container" ref="homeWrapper">

    <!-- UNIFIED FILTER PANEL - Applies to all components -->
    <ReportFilterPanel
      v-show="isInView && !isFilterExcluded"
      :organizations="organizationHierarchy"
      :is-loading="filterLoading"
      :wrapper-top="wrapperTop"
      :brand-data="loadedBrandData"
      @apply-filters="handleFilterApply"
      @filter-changed="handleFilterChange"
      @filters-cleared="handleFiltersClear"
      style="z-index: 998;"
    />

    <h3 class="ea-page-message">
      Congratulations for investing in yourself. Use the following resources to
      guide your continuous development process.
    </h3>

    <!-- ============ MAIN 2-COLUMN GRID ============ -->
    <div class="ea-dashboard-grid">

      <!-- MAIN COLUMN -->
      <div class="ea-main-column">
        <div v-if="shouldRender">
          <HomePDF v-if="loadedPdfData && Object.keys(loadedPdfData).length > 0" label="Personal Development Documents" :pdfData="loadedPdfData" :userData="loadedUserData" :brandData="loadedBrandData"/>
          <HomeAdminTasks v-if="isAdminRole" label="The following tasks are available for you" />

          <HomeSkillBuilding
            label="My Skill Building Actions"
            :userData="loadedUserData"
            :brandData="loadedBrandData"
            :active-filters="effectiveFilters"
            @passSubjectFullName="getSubjectFullName"
          />

          <HomeReports
            key="home-reports-stable"
            label="My Reports Available"
            :userData="loadedUserData"
            :brandData="loadedBrandData"
            :active-filters="effectiveFilters"
          />
        </div>

        <div v-if="role.includes('4')">
          <HomeCohortReports
            reportName="5 Step Profile Report"
            surveyName="Big 5 Survey"
            dateTime="2022-03-23, 9:11:14 AM"
            buttonLabel="View report"
            buttonColor="#e67829"
            label="Your coaching reports available"
            :userData="loadedUserData"
            :brandData="loadedBrandData"
            :active-filters="effectiveFilters"
          />
        </div>

        <div v-if="role.includes('5')">
          <HomeFacultyReport
            reportName="5 Step Profile Report"
            surveyName="Big 5 Survey"
            dateTime="2022-03-23, 9:11:14 AM"
            buttonLabel="View report"
            buttonColor="#e67829"
            label="Your faculty reports"
            :userData="loadedUserData"
            :brandData="loadedBrandData"
            :active-filters="effectiveFilters"
          />
        </div>
      </div>

      </div>
    <!-- ============ END GRID ============ -->

  </div>
</template>

<script>
import api from "../api/api";

export default {
  props: ["userData", "brandData", "pdfData"],
  components: [
    SurveyBox,
    HomeSkillBuilding,
    HomeCohortReports,
    HomeFacultyReport,
    HomeAdminTasks,
    HomeReports,
    HomePDF,
    ReportFilterPanel,
    ExecutiveSidebar,
  ],
  name: "HomeComponent",
  data: () => ({
    headerSubjectFullName: "",
    loadedUserData: null,
    loadedBrandData: null,
    userDataReady: false,
    hasRendered: false,
    loadedPdfData: null,
    // Filter-related data
    filterLoading: true,
    activeFilters: null,
    isInView: false,
    wrapperTop: 0,
    scrollTimer: null,
    intersectionObserver: null,
    allData: [], // Combined data from all components
    excludedFromFilter: [9792, 8], // Add ind_ids that should bypass filters
  }),

  computed: {
    role: function () {
      return this.loadedUserData?.roles ?? "";
    },

    shouldRender: function () {
      if (this.hasRendered) return true;
      return this.role !== '';
    },

    isAdminRole() {
      return this.role.includes('10') ||
        this.role.includes('11') ||
        this.role.includes('12') ||
        this.role.includes('13') ||
        this.role.includes('14') ||
        this.role.includes('18');
    },

    isFilterExcluded() {
      return this.excludedFromFilter.includes(this.loadedUserData?.ind_id);
    },

    effectiveFilters() {
      return this.isFilterExcluded ? null : this.activeFilters;
    },

    organizationHierarchy() {
      const orgMap = new Map();

      // Build hierarchy from all available data
      this.allData.forEach(item => {
        if (!item.org_id) return;

        if (!orgMap.has(item.org_id)) {
          orgMap.set(item.org_id, {
            id: item.org_id,
            name: item.org_name || `Organization ${item.org_id}`,
            subOrgs: new Map()
          });
        }

        const org = orgMap.get(item.org_id);

        if (item.suborg_id) {
          if (!org.subOrgs.has(item.suborg_id)) {
            org.subOrgs.set(item.suborg_id, {
              id: item.suborg_id,
              name: item.suborg_name || `Sub-Org ${item.suborg_id}`,
              programs: new Map()
            });
          }

          const subOrg = org.subOrgs.get(item.suborg_id);

          if (item.program_id) {
            if (!subOrg.programs.has(item.program_id)) {
              subOrg.programs.set(item.program_id, {
                id: item.program_id,
                name: item.program_name || `Program ${item.program_id}`,
                iterations: new Map()
              });
            }

            const program = subOrg.programs.get(item.program_id);

            if (item.iteration_id) {
              if (!program.iterations.has(item.iteration_id)) {
                program.iterations.set(item.iteration_id, {
                  id: item.iteration_id,
                  name: item.iteration_name || `Iteration ${item.iteration_id}`,
                  streams: new Map()
                });
              }

              const iteration = program.iterations.get(item.iteration_id);

              if (item.stream_id) {
                if (!iteration.streams.has(item.stream_id)) {
                  iteration.streams.set(item.stream_id, {
                    id: item.stream_id,
                    name: item.stream_name || `Stream ${item.stream_id}`,
                    groups: new Map()
                  });
                }

                const stream = iteration.streams.get(item.stream_id);

                if (item.group_id) {
                  if (!stream.groups.has(item.group_id)) {
                    stream.groups.set(item.group_id, {
                      id: item.group_id,
                      name: item.group_name || `Group ${item.group_id}`
                    });
                  }
                }
              }
            }
          }
        }
      });

      return Array.from(orgMap.values()).map(org => ({
        ...org,
        subOrgs: Array.from(org.subOrgs.values()).map(subOrg => ({
          ...subOrg,
          programs: Array.from(subOrg.programs.values()).map(program => ({
            ...program,
            iterations: Array.from(program.iterations.values()).map(iteration => ({
              ...iteration,
              streams: Array.from(iteration.streams.values()).map(stream => ({
                ...stream,
                groups: Array.from(stream.groups.values())
              }))
            }))
          }))
        }))
      }));
    }
  },

  async mounted() {
    if(this.userData && this.userData.ind_id){
      this.loadedUserData = this.userData;
      this.userDataReady = true;
    } else {
      this.checkLoginAndBrandData();
      if(this.loadedUserData?.ind_id) {
        this.userDataReady = true;
      }
    }

    if(this.brandData && this.brandData.main_color1){
      this.loadedBrandData = this.brandData;
    }

    if(this.pdfData){
      this.loadedPdfData = this.pdfData;
    }

    // Load all data for filter hierarchy
    await this.loadAllData();

    // Setup visibility detection
    this.$nextTick(() => {
      this.setupIntersectionObserver();
      this.setupScrollListener();
    });
  },

  methods: {
    async loadAllData() {
      this.filterLoading = true;
      try {
        const userId = this.loadedUserData.ind_id;

        // Load data from all relevant endpoints
        const promises = [];

        // Survey assignments (for HomeSkillBuilding)
        promises.push(
          api.get(`survey-assignments/individuals/${userId}`)
            .then(res => res.data)
            .catch(() => [])
        );

        // Reports (for HomeReports)
        promises.push(
          api.get(`get-participant-reports/${userId}`)
            .then(res => res.data)
            .catch(() => [])
        );

        // Coach reports if applicable (for HomeCohortReports)
        if (this.role.includes('4')) {
          promises.push(
            api.get(`get-coach-reports/${userId}`)
              .then(res => res.data)
              .catch(() => [])
          );
        }

        // Faculty reports if applicable (for HomeFacultyReport)
        if (this.role.includes('5')) {
          const array = this.loadedUserData.suborgs.split(",");
          const joinSuborg = array.join(",");
          promises.push(
            api.get(`get-faculty-reports/${this.loadedUserData.org_id}/${joinSuborg}`)
              .then(res => res.data)
              .catch(() => [])
          );
        }

        const results = await Promise.all(promises);
        this.allData = results.flat();

      } catch (error) {
        console.error('Error loading data for filters:', error);
      } finally {
        this.filterLoading = false;
      }
    },

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.5, 0.9, 1.0]
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isInView = entry.isIntersecting;
          if (this.isInView) {
            this.updateWrapperPosition();
          }
        });
      }, options);

      if (this.$refs.homeWrapper) {
        this.intersectionObserver.observe(this.$refs.homeWrapper);
      }
    },

    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.updateWrapperPosition();
    },

    handleScroll() {
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer);
      }
      this.scrollTimer = requestAnimationFrame(() => {
        if (this.isInView) {
          this.updateWrapperPosition();
        }
      });
    },

    handleResize() {
      if (this.isInView) {
        this.updateWrapperPosition();
      }
    },

    updateWrapperPosition() {
      if (!this.$refs.homeWrapper) return;

      const wrapper = this.$refs.homeWrapper;
      const rect = wrapper.getBoundingClientRect();
      const wrapperHeight = rect.height;
      const targetPosition = wrapperHeight * 0.4;
      this.wrapperTop = Math.max(0, rect.top + targetPosition);
    },

    handleFilterApply(filters) {
      this.activeFilters = filters;
    },

    handleFilterChange() {
      // Optional: for real-time filtering
    },

    handleFiltersClear() {
      this.activeFilters = null;
    },

    checkLoginAndBrandData() {
      const loginData = this.getCookie('passLoginUp')
      if (loginData && !this.loadedUserData?.ind_id) {
        this.loadedUserData = JSON.parse(loginData)
      }

      const brandData = this.getCookie('passBrandUp')
      if (brandData && !this.loadedBrandData?.main_color1) {
        this.loadedBrandData = JSON.parse(brandData)
      }
    },

    setCookie(name, value, hours) {
      const expires = new Date()
      expires.setTime(expires.getTime() + (hours * 60 * 60 * 1000))
      const encodedValue = encodeURIComponent(value)
      document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;secure;sameSite=Lax`
    },

    getCookie(name) {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [cookieName, ...cookieParts] = cookie.split('=').map(c => c.trim());
        const cookieValue = cookieParts.join('=');
        if (cookieName === name) {
          try {
            const decodedValue = decodeURIComponent(cookieValue);
            if (decodedValue === null || decodedValue === undefined) {
              return null;
            }
            return decodedValue;
          } catch (e) {
            return null;
          }
        }
      }
      return null;
    },

    getSubjectFullName(data) {
      this.headerSubjectFullName = data;
      this.$emit("passHeaderSubjectFullName", this.headerSubjectFullName);
    },
  },

  beforeUnmount() {
    if (this.intersectionObserver && this.$refs.homeWrapper) {
      this.intersectionObserver.unobserve(this.$refs.homeWrapper);
      this.intersectionObserver.disconnect();
    }

    if (this.scrollTimer) {
      cancelAnimationFrame(this.scrollTimer);
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },

  watch: {
    role(newVal) {
      if (newVal !== '') this.hasRendered = true;
    },
    userData(newVal) {
      if (newVal && newVal.ind_id && !this.loadedUserData?.ind_id) {
        this.loadedUserData = newVal;
        this.userDataReady = true;
      }
    },
    brandData(newVal) {
      if (newVal && newVal.main_color1 && !this.loadedBrandData?.main_color1) {
        this.loadedBrandData = newVal;
      }
    },
    pdfData(newVal) {
      if (newVal) {
        this.loadedPdfData = this.pdfData
      }
    },
  },
};
</script>

<style scoped>
.body-form-container {
  position: relative;
  min-height: 100vh;
  background: var(--background, #f8f9ff);
  padding-bottom: var(--space-xl, 48px);
}

.ea-page-message {
  text-align: center;
  padding: var(--space-md, 24px) var(--space-sm, 16px);
  font-family: var(--font-family, 'Inter', sans-serif);
  font-size: var(--headline-lg-size, 24px);
  font-weight: var(--headline-lg-weight, 600);
  line-height: var(--headline-lg-line, 1.3);
  letter-spacing: var(--headline-lg-tracking, -0.01em);
  color: var(--primary, #002955);
  max-width: 900px;
  margin: 0 auto;
}

/* ===== 2-COLUMN EXECUTIVE GRID ===== */
.ea-dashboard-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--gutter, 24px);
  max-width: var(--container-max, 1440px);
  margin: 0 auto;
  padding: 0 var(--space-lg, 32px);
}

.ea-main-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm, 16px);
}

.ea-sidebar-column {
  min-width: 0;
}

/* Tablet: sidebar drops below, cards stack */
@media only screen and (min-width: 768px) and (max-width: 1199px) {
  .ea-dashboard-grid {
    grid-template-columns: 1fr;
    padding: 0 var(--space-md, 24px);
  }
}

/* Mobile */
@media only screen and (min-width: 280px) and (max-width: 767px) {
  .ea-dashboard-grid {
    grid-template-columns: 1fr;
    padding: 0 16px;
    gap: var(--space-sm, 16px);
  }
  .ea-page-message {
    font-size: var(--headline-lg-mobile-size, 20px);
    padding: var(--space-sm, 16px) var(--space-xs, 8px);
  }
}
</style>
